import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Palette, Copy, Check, Plus, Minus } from '../icons';
import type { RGB } from '../../types';
import type { HSV, HarmonyMode, ColorWheelProps } from './types';
import { HARMONY_MODES, MAX_HANDLES } from './types';
import {
  getHarmonyHandles,
  updateCustomHandle,
  getConstrainedDrag,
  getComplementIndex,
  getEndIndices,
  enforceHandleCount,
  getMinHandles,
} from './harmonyRules';
import { rgbToHsv, hsvToRgb, rgbToHex } from '../../utils/colors';
import { copyResult } from '../../utils/analytics';
import WheelCanvas from './WheelCanvas';
import MagnifierCanvas from './MagnifierCanvas';
import ColorSwatchRow from './ColorSwatchRow';
import ShadesBrightnessBar from './ShadesBrightnessBar';

const MAX_WHEEL_SIZE = 380;

/** Measures available width via ResizeObserver and returns a capped wheel size. */
const useWheelSize = (maxSize: number): [React.RefCallback<HTMLElement>, number] => {
  const [size, setSize] = useState(maxSize);
  const observerRef = useRef<ResizeObserver | null>(null);

  const refCallback = useCallback((node: HTMLElement | null) => {
    // Cleanup previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!node) return;

    const measure = () => {
      const width = node.clientWidth;
      if (width > 0) setSize(Math.min(maxSize, width));
    };
    measure(); // initial measurement

    observerRef.current = new ResizeObserver(measure);
    observerRef.current.observe(node);
  }, [maxSize]);

  return [refCallback, size];
};

const toHSV = (rgb: RGB): HSV => rgbToHsv(rgb.r, rgb.g, rgb.b, false);
const toRGB = (hsv: HSV): RGB => hsvToRgb(hsv.h, hsv.s, hsv.v);
const wrapHue = (h: number): number => ((h % 360) + 360) % 360;

/** Modes where dependent brightness slider is enabled */
const BRIGHTNESS_ENABLED_MODES: ReadonlySet<string> = new Set([
  'monochromatic', 'shades', 'custom', 'compound',
]);

/** Modes where dependent handles cannot be dragged on the wheel */
const NO_WHEEL_DRAG_MODES: ReadonlySet<string> = new Set(['shades']);

/** Modes that use customColors state for per-handle positions */
const CUSTOM_COLOR_MODES: ReadonlySet<string> = new Set([
  'custom', 'compound', 'triad', 'square', 'complementary', 'shades',
]);

/** Modes where base rotation should delta-link extra handles */
const DELTA_LINK_MODES: ReadonlySet<string> = new Set([
  'compound', 'triad', 'square', 'complementary',
]);

/** Core handle count per mode (extras beyond this are user-positioned) */
const CORE_HANDLE_COUNTS: Partial<Record<string, number>> = {
  triad: 3,
  square: 4,
  complementary: 2,
};

const ColorWheel: React.FC<ColorWheelProps> = ({
  baseColor,
  onBaseColorChange,
  onHarmonyChange,
  initialHandleCount,
  maxHandleCount = MAX_HANDLES,
  colorName,
}) => {
  const [wheelContainerRef, wheelSize] = useWheelSize(MAX_WHEEL_SIZE);
  const [mode, setMode] = useState<HarmonyMode>('analogous');
  const [activeIndex, setActiveIndex] = useState(0);
  const [copied, setCopied] = useState<number | null>(null);
  const [copyError, setCopyError] = useState('');
  const [handleCount, setHandleCount] = useState(() =>
    enforceHandleCount('analogous', initialHandleCount ?? 5, maxHandleCount),
  );
  const [isDragging, setIsDragging] = useState(false);
  const [dragCoords, setDragCoords] = useState<{ x: number; y: number } | null>(null);
  const [wheelBitmap, setWheelBitmap] = useState<HTMLCanvasElement | null>(null);

  // Monochromatic: end handle position
  const [monoEnd, setMonoEnd] = useState<HSV | null>(null);

  // Monochromatic: per-handle overrides for middle handles (null = use interpolation)
  const [monoMiddles, setMonoMiddles] = useState<(HSV | null)[]>([]);

  // Custom / Compound: independent handle positions
  const [customColors, setCustomColors] = useState<HSV[] | null>(null);

  // Track previous base H/S for delta linking (compound uses both for relative positioning)
  const prevBaseHSRef = useRef<{ h: number; s: number } | null>(null);

  // Split mode: per-pair angular offset from complement axis
  const splitOffsetsRef = useRef<number[] | null>(null);

  // Split mode: per-handle saturation (independent from mirror pair)
  const splitSaturationsRef = useRef<number[] | null>(null);

  // Store intended H/S before every onBaseColorChange call to avoid HSV→RGB→HSV round-trip loss.
  // This completely eliminates H/S drift regardless of V level.
  const intendedHSRef = useRef<{ h: number; s: number } | null>(null);

  // Drag-lock: snapshot all handles' H/S at pointerdown on brightness slider.
  // During drag, locked H/S is used instead of displayColors' (potentially drifted) values.
  const lockedHSRef = useRef<Map<number, { h: number; s: number }> | null>(null);

  /** Always use this instead of raw onBaseColorChange. Preserves H/S precision. */
  const setBaseHSV = useCallback((hsv: HSV) => {
    intendedHSRef.current = { h: hsv.h, s: hsv.s };
    onBaseColorChange(toRGB(hsv));
  }, [onBaseColorChange]);

  // Derive base HSV from prop — use intended H/S when available to avoid round-trip errors.
  const baseHSV = useMemo(() => {
    const raw = toHSV(baseColor);
    const intended = intendedHSRef.current;
    if (intended) {
      // Our own change: use exact H/S, only V from round-trip
      intendedHSRef.current = null;
      return { h: intended.h, s: intended.s, v: raw.v };
    }
    // External change (parent changed baseColor directly): use raw conversion
    return raw;
  }, [baseColor]);

  // Delta linking: when base moves, shift extra handles to maintain relative position.
  // - All DELTA_LINK_MODES: apply hue rotation delta
  // - Compound additionally: apply proportional saturation scaling
  useEffect(() => {
    const prev = prevBaseHSRef.current;
    if (DELTA_LINK_MODES.has(mode) && customColors && prev) {
      let hueDelta = baseHSV.h - prev.h;
      if (hueDelta > 180) hueDelta -= 360;
      if (hueDelta < -180) hueDelta += 360;
      const satScale = prev.s > 0 ? baseHSV.s / prev.s : 1;
      const needsHue = Math.abs(hueDelta) > 0.1;
      const needsSat = mode === 'compound' && Math.abs(satScale - 1) > 0.01;

      if (needsHue || needsSat) {
        const compIdx = getComplementIndex(mode, customColors);
        const coreCount = CORE_HANDLE_COUNTS[mode] ?? 0;
        setCustomColors(prevColors => {
          if (!prevColors) return prevColors;
          return prevColors.map((c, i) => {
            if (i === 0) return c;
            if (mode === 'compound' && i === compIdx) return c;
            if (coreCount > 0 && i < coreCount) return c;
            const newH = needsHue ? wrapHue(c.h + hueDelta) : c.h;
            const newS = needsSat ? Math.max(0, Math.min(100, Math.round(c.s * satScale))) : c.s;
            return { ...c, h: newH, s: newS };
          });
        });
      }
    }
    prevBaseHSRef.current = { h: baseHSV.h, s: baseHSV.s };
  }, [baseHSV.h, baseHSV.s, mode]);

  // Rule-based harmony colors
  const ruleColors = useMemo((): HSV[] => {
    if (mode === 'monochromatic') {
      // Sync monoEnd hue to base (mono = same hue, varying S/V)
      const syncedEnd = monoEnd ? { ...monoEnd, h: baseHSV.h } : undefined;
      return getHarmonyHandles(mode, baseHSV, handleCount, syncedEnd);
    }

    // For modes with customColors: hybrid approach
    // Core handles recalculated from harmony rule, extras from customColors
    if (CUSTOM_COLOR_MODES.has(mode)) {
      const ruleGenerated = getHarmonyHandles(mode, baseHSV, handleCount);

      if (!customColors) {
        setCustomColors(ruleGenerated);
        return ruleGenerated;
      }

      const coreCount = CORE_HANDLE_COUNTS[mode] ?? 0;

      // custom: fully user-controlled
      if (mode === 'custom') {
        return [baseHSV, ...customColors.slice(1)];
      }

      // compound: base + C always as complement + rest from customColors
      if (mode === 'compound') {
        const compIdx = getComplementIndex(mode, customColors);
        const compHSV: HSV = { h: wrapHue(baseHSV.h + 180), s: baseHSV.s, v: baseHSV.v };
        return customColors.map((c, i) => {
          if (i === 0) return baseHSV;
          if (i === compIdx) return compHSV;
          return c;
        });
      }

      // shades: all handles use base H/S, only V from customColors
      if (mode === 'shades') {
        return Array.from({ length: handleCount }, (_, i) => ({
          h: baseHSV.h,
          s: baseHSV.s,
          v: customColors[i]?.v ?? ruleGenerated[i]?.v ?? baseHSV.v,
        }));
      }

      // triad/square/complementary: core from rule, extras from customColors
      const result: HSV[] = [];
      for (let i = 0; i < handleCount; i++) {
        if (i < coreCount) {
          // Core handles: always from harmony rule
          result.push(ruleGenerated[i] ?? baseHSV);
        } else {
          // Extra handles: from customColors if available, else rule default
          result.push(customColors[i] ?? ruleGenerated[i] ?? baseHSV);
        }
      }
      return result;
    }

    return getHarmonyHandles(mode, baseHSV, handleCount);
  }, [baseHSV, mode, handleCount, customColors, monoEnd]);

  // Apply mono middle overrides (sync hue to base when B moves)
  const harmonyColors = useMemo((): HSV[] => {
    if (mode === 'monochromatic' && monoMiddles.length > 0) {
      return ruleColors.map((c, i) => {
        if (i === 0 || i === ruleColors.length - 1) return c; // B and E stay
        const override = monoMiddles[i];
        if (!override) return c;
        // Mono: hue must always match base — only keep the S/V override position
        // Recalculate position on B→E line at the same relative t
        return { ...override, h: baseHSV.h };
      });
    }
    return ruleColors;
  }, [ruleColors, monoMiddles, mode, baseHSV.h]);

  // Reset mode-specific state when switching modes
  useEffect(() => {
    if (!CUSTOM_COLOR_MODES.has(mode)) setCustomColors(null);
    if (mode !== 'monochromatic') {
      setMonoEnd(null);
      setMonoMiddles([]);
    }
    if (mode !== 'split-complementary') {
      splitOffsetsRef.current = null;
      splitSaturationsRef.current = null;
    }
  }, [mode]);

  // ─── Event Handlers ───

  const resetModeState = useCallback(() => {
    setActiveIndex(0);
    setCustomColors(null);
    setMonoEnd(null);
    setMonoMiddles([]);
    splitOffsetsRef.current = null;
    splitSaturationsRef.current = null;
    prevBaseHSRef.current = null;
  }, []);

  const handleModeChange = useCallback((newMode: HarmonyMode) => {
    const enforced = enforceHandleCount(newMode, handleCount, maxHandleCount);
    setMode(newMode);
    setHandleCount(enforced);
    resetModeState();
  }, [handleCount, resetModeState, maxHandleCount]);

  const handleCountChange = useCallback((delta: number) => {
    setHandleCount((prev) => {
      const raw = prev + (mode === 'split-complementary' ? delta * 2 : delta);
      const next = enforceHandleCount(mode, raw, maxHandleCount);
      if (next === prev) return prev;
      resetModeState();
      return next;
    });
  }, [mode, resetModeState, maxHandleCount]);

  const handleHandleDrag = useCallback(
    (index: number, newHsv: HSV) => {
      if (index === 0) {
        setBaseHSV(newHsv);
        return;
      }

      const constraint = getConstrainedDrag(mode, harmonyColors, index, newHsv);

      switch (constraint.type) {
        case 'rotate': {
          const newBaseHue = wrapHue(baseHSV.h + constraint.delta);
          setBaseHSV({ ...baseHSV, h: newBaseHue });
          break;
        }
        case 'mono-end': {
          setMonoEnd(constraint.hsv);
          break;
        }
        case 'mono-line': {
          // Middle handle freely slides on B↔E line
          setMonoMiddles(prev => {
            const next = [...prev];
            // Ensure array is big enough
            while (next.length <= index) next.push(null);
            next[index] = constraint.hsv;
            return next;
          });
          break;
        }
        case 'spoke': {
          // Extra on spoke — persist position in customColors
          const spokeUpdated = updateCustomHandle(
            customColors || harmonyColors,
            index,
            constraint.hsv,
          );
          setCustomColors(spokeUpdated);
          break;
        }
        case 'diameter-line': {
          // Complement extras on the diameter line — persist position
          const diaUpdated = updateCustomHandle(
            customColors || harmonyColors,
            index,
            constraint.hsv,
          );
          setCustomColors(diaUpdated);
          break;
        }
        case 'mirror': {
          const { delta, mirrorIndex } = constraint;
          const compAxis = wrapHue(baseHSV.h + 180);
          if (!splitOffsetsRef.current) {
            splitOffsetsRef.current = harmonyColors.map((c, i) => {
              if (i === 0) return 0;
              let off = c.h - compAxis;
              if (off > 180) off -= 360;
              if (off < -180) off += 360;
              return off;
            });
          }
          // Initialize saturation tracking if needed
          if (!splitSaturationsRef.current) {
            splitSaturationsRef.current = harmonyColors.map(c => c.s);
          }
          splitOffsetsRef.current[index] = delta;
          splitOffsetsRef.current[mirrorIndex] = -delta;
          // Store saturation independently for the dragged handle
          splitSaturationsRef.current[index] = newHsv.s;
          setBaseHSV(baseHSV);
          break;
        }
        case 'free': {
          // Custom and Compound non-B/C: update individual handle
          const updated = updateCustomHandle(
            customColors || harmonyColors,
            index,
            constraint.hsv,
          );
          setCustomColors(updated);
          break;
        }
        case 'none':
        default:
          break;
      }
    },
    [mode, harmonyColors, baseHSV, setBaseHSV, customColors],
  );

  // Apply split offsets and saturations
  const displayColors = useMemo((): HSV[] => {
    if (mode === 'split-complementary' && splitOffsetsRef.current) {
      const compAxis = wrapHue(baseHSV.h + 180);
      return harmonyColors.map((c, i) => {
        if (i === 0) return c;
        const offset = splitOffsetsRef.current?.[i];
        if (offset === undefined) return c;
        const sat = splitSaturationsRef.current?.[i] ?? c.s;
        return { ...c, h: wrapHue(compAxis + offset), s: sat };
      });
    }
    return harmonyColors;
  }, [harmonyColors, mode, baseHSV]);

  // Notify parent of harmony changes (use displayColors which includes split offsets)
  useEffect(() => {
    onHarmonyChange(displayColors.map(toRGB));
  }, [displayColors, onHarmonyChange]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    setDragCoords(null);
  }, []);

  const handleDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleDragMove = useCallback((x: number, y: number) => {
    setDragCoords({ x, y });
  }, []);

  const handleBitmapReady = useCallback((canvas: HTMLCanvasElement) => {
    setWheelBitmap(canvas);
  }, []);

  /** Lock all handles' H/S at drag start to prevent round-trip drift. */
  const lockAllHS = useCallback(() => {
    lockedHSRef.current = new Map(
      displayColors.map((c, i) => [i, { h: c.h, s: c.s }]),
    );
  }, [displayColors]);

  /** Release H/S lock at drag end. */
  const unlockHS = useCallback(() => {
    lockedHSRef.current = null;
  }, []);

  const handleBrightnessChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = parseInt(e.target.value, 10);
      const target = displayColors[activeIndex];
      const locked = lockedHSRef.current?.get(activeIndex);
      const newHsv: HSV = { h: locked?.h ?? target.h, s: locked?.s ?? target.s, v };

      if (activeIndex === 0) {
        setBaseHSV(newHsv);
      } else if (CUSTOM_COLOR_MODES.has(mode)) {
        const updated = updateCustomHandle(
          customColors || displayColors,
          activeIndex,
          newHsv,
        );
        setCustomColors(updated);
      } else if (mode === 'monochromatic') {
        if (activeIndex === displayColors.length - 1) {
          setMonoEnd(newHsv);
        } else {
          setMonoMiddles(prev => {
            const next = [...prev];
            while (next.length <= activeIndex) next.push(null);
            next[activeIndex] = newHsv;
            return next;
          });
        }
      }
    },
    [activeIndex, displayColors, mode, setBaseHSV, customColors],
  );

  // Shades mode: per-handle brightness change from multi-marker bar
  const handleShadeBrightnessChange = useCallback(
    (index: number, v: number) => {
      const target = displayColors[index];
      const locked = lockedHSRef.current?.get(index);
      const newHsv: HSV = { h: locked?.h ?? target.h, s: locked?.s ?? target.s, v };
      if (index === 0) {
        setBaseHSV(newHsv);
      } else {
        const updated = updateCustomHandle(
          customColors || displayColors,
          index,
          newHsv,
        );
        setCustomColors(updated);
      }
    },
    [displayColors, setBaseHSV, customColors],
  );

  const handleCopyHex = useCallback(async (index: number) => {
    const rgb = toRGB(displayColors[index]);
    const hex = rgbToHex(rgb.r, rgb.g, rgb.b);
    const ok = await copyResult(hex, 'color_wheel');
    setCopyError(ok ? '' : 'Clipboard unavailable. Select the displayed HEX value to copy it.');
    if (!ok) return;
    setCopied(index);
    setTimeout(() => setCopied(null), 1500);
  }, [displayColors]);

  // ─── Derived Values ───

  const activeColor = displayColors[activeIndex] || displayColors[0];
  const activeRGB = toRGB(activeColor);
  const activeHex = rgbToHex(activeRGB.r, activeRGB.g, activeRGB.b);
  const baseBrightness = baseHSV.v;

  const canDragDependents = !NO_WHEEL_DRAG_MODES.has(mode);
  const brightnessEnabled = activeIndex === 0 || BRIGHTNESS_ENABLED_MODES.has(mode);

  const complementIndex = getComplementIndex(mode, displayColors);
  const endIndices = getEndIndices(mode, handleCount);
  const minHandles = getMinHandles(mode);

  return (
    <div className="ui-panel p-5 md:p-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-3 sm:mb-5">
        <Palette className="text-brand-600 shrink-0" size={22} />
        <h2 className="text-xl font-bold text-ink">Color Wheel</h2>
      </div>

      {/* Harmony Mode Selector + Handle Count */}
      <div className="flex flex-row gap-3 mb-4 sm:mb-6">
        <label className="sm:hidden flex-1 min-w-0"><span className="sr-only">Color harmony</span><select aria-label="Color harmony" value={mode} onChange={e => handleModeChange(e.target.value as HarmonyMode)} className="ui-input w-full">{HARMONY_MODES.map(item => <option key={item.mode} value={item.mode}>{item.label}</option>)}</select></label>
        <div className="hidden sm:flex flex-wrap gap-1.5 flex-1 p-1.5 bg-surface-sunken rounded-xl">
          {HARMONY_MODES.map(({ mode: m, label }) => (
            <button
              key={m}
              onClick={() => handleModeChange(m)}
              aria-pressed={mode === m}
              className={`
                min-h-11 px-3 py-2 rounded-control text-sm font-medium transition-colors
                ${mode === m
                  ? 'bg-white text-brand-700 shadow-sm font-semibold'
                  : 'text-ink-secondary hover:text-ink hover:bg-white/70'
                }
              `}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Handle Count Control */}
        <div className="flex items-center gap-1.5 bg-surface-sunken rounded-xl p-1 shrink-0">
          <button
            onClick={() => handleCountChange(-1)}
            disabled={handleCount <= minHandles}
            className="w-9 h-11 flex items-center justify-center rounded-control text-ink-secondary hover:text-ink hover:bg-white/50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            title="Remove handle"
          >
            <Minus size={14} />
          </button>
          <span className="text-xs font-medium text-ink tabular-nums min-w-[2.5rem] text-center">
            {handleCount}
          </span>
          <button
            onClick={() => handleCountChange(1)}
            disabled={enforceHandleCount(mode, handleCount + 1, maxHandleCount) === handleCount}
            className="w-9 h-11 flex items-center justify-center rounded-control text-ink-secondary hover:text-ink hover:bg-white/50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            title="Add handle"
          >
            <Plus size={14} />
          </button>
        </div>
      </div>

      {/* Mode description */}
      <p className="hidden sm:block text-xs text-ink-secondary leading-relaxed mb-1">
        {HARMONY_MODES.find(m => m.mode === mode)?.description}
        {colorName && ` Explore ${mode} harmonies for ${colorName}.`}
      </p>

      {/* Wheel + Controls */}
      <div className="flex flex-col lg:flex-row gap-6 items-center">
        {/* Canvas + Magnifier */}
        <div ref={wheelContainerRef} className="flex justify-center relative w-full" style={{ maxWidth: MAX_WHEEL_SIZE }}>
          <WheelCanvas
            size={wheelSize}
            brightness={baseBrightness}
            handles={displayColors}
            activeIndex={activeIndex}
            canDragAll={canDragDependents}
            mode={mode}
            complementIndex={complementIndex}
            endIndices={endIndices}
            onHandleDrag={handleHandleDrag}
            onDragEnd={handleDragEnd}
            onActiveChange={setActiveIndex}
            onDragStart={handleDragStart}
            onDragMove={handleDragMove}
            onBitmapReady={handleBitmapReady}
          />
          <MagnifierCanvas
            wheelBitmap={wheelBitmap}
            pointerX={dragCoords?.x ?? 0}
            pointerY={dragCoords?.y ?? 0}
            visible={isDragging && dragCoords !== null}
          />
        </div>

        {/* Side controls */}
        <div className="flex-1 w-full min-w-0 space-y-5">
          {/* Active color info */}
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-xl border border-surface-border shadow-inner"
              style={{ backgroundColor: activeHex }}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-mono text-lg font-bold text-ink">
                  {activeHex.toUpperCase()}
                </span>
                <button
                  onClick={() => handleCopyHex(activeIndex)}
                  className="p-1 text-ink-muted hover:text-brand-600 transition-colors"
                  title="Copy hex"
                >
                  {copied === activeIndex ? (
                    <Check size={14} className="text-emerald-500" />
                  ) : (
                    <Copy size={14} />
                  )}
                </button>
              </div>
              <span className="text-xs text-ink-secondary">
                H: {Math.round(activeColor.h)}° · S: {Math.round(activeColor.s)}% · B: {Math.round(activeColor.v)}%
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3"><label className="text-xs font-medium text-ink-secondary">Base hue (degrees)<input aria-label="Base hue" type="number" min={0} max={359} value={Math.round(baseHSV.h)} onChange={e => { if (e.target.value !== '') setBaseHSV({ ...baseHSV, h: Math.max(0, Math.min(359, Number(e.target.value))) }); }} className="block w-full border border-surface-border rounded-lg p-2 mt-1 text-ink"/></label><label className="text-xs font-medium text-ink-secondary">Base saturation (%)<input aria-label="Base saturation" type="number" min={0} max={100} value={Math.round(baseHSV.s)} onChange={e => { if (e.target.value !== '') setBaseHSV({ ...baseHSV, s: Math.max(0, Math.min(100, Number(e.target.value))) }); }} className="block w-full border border-surface-border rounded-lg p-2 mt-1 text-ink"/></label></div>
          {/* Brightness control */}
          {mode === 'shades' ? (
            <ShadesBrightnessBar
              shades={displayColors}
              activeIndex={activeIndex}
              onBrightnessChange={handleShadeBrightnessChange}
              onActiveChange={setActiveIndex}
              onDragStart={lockAllHS}
              onDragEnd={unlockHS}
            />
          ) : (
            <div className="space-y-2">
              <div className="flex justify-between text-xs font-medium text-ink-secondary uppercase tracking-wider">
                <span>Brightness</span>
                <span className="font-mono">{Math.round(activeColor.v)}%</span>
              </div>
              <input
                type="range"
                aria-label="Active color brightness"
                min={5}
                max={100}
                value={Math.round(activeColor.v)}
                onChange={handleBrightnessChange}
                onPointerDown={lockAllHS}
                onPointerUp={unlockHS}
                onPointerCancel={unlockHS}
                disabled={!brightnessEnabled}
                className="w-full accent-indigo-500"
                style={{
                  background: `linear-gradient(to right, #000 0%, ${rgbToHex(
                    hsvToRgb(activeColor.h, activeColor.s, 100).r,
                    hsvToRgb(activeColor.h, activeColor.s, 100).g,
                    hsvToRgb(activeColor.h, activeColor.s, 100).b,
                  )} 100%)`,
                }}
              />
              {!brightnessEnabled && (
                <p className="text-xs text-ink-muted">
                  This mode sets brightness based on harmony rules
                </p>
              )}
            </div>
          )}

          {/* Quick palette copy */}
          <div className="pt-2 border-t border-surface-border">
            <p className="text-xs text-ink-muted uppercase tracking-wider mb-2">
              Click a swatch to select · Click hex to copy
            </p>
            <div className="flex flex-wrap gap-2">
              {displayColors.map((hsv, i) => {
                const rgb = toRGB(hsv);
                const hex = rgbToHex(rgb.r, rgb.g, rgb.b);
                return (
                  <button
                    key={i}
                    onClick={() => handleCopyHex(i)}
                    className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-surface-sunken hover:bg-surface text-xs font-mono text-ink-secondary transition-colors"
                    title={`Copy ${hex}`}
                  >
                    <span
                      className="w-3 h-3 rounded-sm border border-surface-border-strong"
                      style={{ backgroundColor: hex }}
                    />
                    {copied === i ? (
                      <Check size={10} className="text-emerald-500" />
                    ) : (
                      hex.toUpperCase()
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <p role="status" className="text-sm text-red-700">{copyError}</p>
      <p className="sm:hidden text-xs text-ink-secondary leading-relaxed mt-5">{HARMONY_MODES.find(m => m.mode === mode)?.description}</p>
      {/* Swatch Row */}
      <div className="mt-6">
        <ColorSwatchRow
          colors={displayColors}
          activeIndex={activeIndex}
          onSelect={setActiveIndex}
        />
      </div>
    </div>
  );
};

export default ColorWheel;
